import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { companyInfo } from '../../data/mockData';
import { Menu, X, Facebook, Linkedin, Phone, ChevronDown } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services', hasDropdown: true },
    { label: 'Industries', path: '/industries' },
    { label: 'Request a Quote', path: '/quote' },
    { label: 'Offices', path: '/offices' },
    { label: 'Contact', path: '/contact' },
    { label: 'Employment Opportunity', path: '/employment' },
  ];

  const serviceItems = [
    { label: 'Unarmed Guards', path: '/services/unarmed-guards' },
    { label: 'Armed Guards', path: '/services/armed-guards' },
    { label: 'Mobile Patrols', path: '/services/mobile-patrols' },
    { label: 'Event Security', path: '/services/event-security' },
    { label: 'Employee Termination', path: '/services/employee-termination' },
    { label: 'Surveillance Monitoring', path: '/services/surveillance-monitoring' },
  ];

  return (
    <header className="fixed w-full z-50">
      {/* Top Bar */}
      <div className="bg-[#1e3a52] py-2">
        <div className="max-w-7xl mx-auto px-4 flex justify-end items-center gap-4">
          <div className="flex items-center gap-3">
            <a href={companyInfo.socialLinks.facebook} target="_blank" rel="noopener noreferrer" 
               className="text-white hover:text-[#5ba4c8] transition-colors">
              <Facebook size={18} />
            </a>
            <a href={companyInfo.socialLinks.linkedin} target="_blank" rel="noopener noreferrer"
               className="text-white hover:text-[#5ba4c8] transition-colors">
              <Linkedin size={18} />
            </a>
            <span className="text-white text-sm">✦</span>
          </div>
          <a href={`tel:${companyInfo.phone}`} 
             className="bg-[#5ba4c8] text-white px-4 py-1 text-sm font-medium hover:bg-[#4a93b7] transition-colors">
            {companyInfo.phone}
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-[#2c4a62]/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-b from-[#5ba4c8] to-[#2c4a62] rounded-md flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 2.18l7 3.12v5.7c0 4.67-3.13 8.97-7 10.2-3.87-1.23-7-5.53-7-10.2V6.3l7-3.12z"/>
                  <path d="M12 6l-4 2v3c0 2.5 1.7 4.8 4 5.5 2.3-.7 4-3 4-5.5V8l-4-2z"/>
                </svg>
              </div>
              <span className="text-white font-bold text-sm tracking-wider hidden sm:block">
                BU SECURITY
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {navItems.map((item) => (
                <div key={item.path} className="relative group">
                  {item.hasDropdown ? (
                    <div
                      className="flex items-center gap-1 cursor-pointer"
                      onMouseEnter={() => setServicesOpen(true)}
                      onMouseLeave={() => setServicesOpen(false)}
                    >
                      <Link
                        to={item.path}
                        className={`text-sm font-medium transition-colors ${
                          isActive(item.path) || location.pathname.startsWith('/services/')
                            ? 'text-[#5ba4c8]'
                            : 'text-white hover:text-[#5ba4c8]'
                        }`}
                      >
                        {item.label}
                      </Link>
                      <ChevronDown size={14} className="text-white" />
                      
                      {/* Dropdown */}
                      {servicesOpen && (
                        <div className="absolute top-full left-0 mt-2 w-56 bg-[#2c4a62] shadow-lg rounded-md py-2 z-50">
                          {serviceItems.map((service) => (
                            <Link
                              key={service.path}
                              to={service.path}
                              className="block px-4 py-2 text-sm text-white hover:bg-[#5ba4c8] hover:text-white transition-colors"
                            >
                              {service.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`text-sm font-medium transition-colors ${
                        isActive(item.path)
                          ? 'text-[#5ba4c8]'
                          : 'text-white hover:text-[#5ba4c8]'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#2c4a62] border-t border-[#5ba4c8]/20">
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <div key={item.path}>
                  <Link
                    to={item.path}
                    className={`block py-2 text-sm font-medium ${
                      isActive(item.path) ? 'text-[#5ba4c8]' : 'text-white'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                  {item.hasDropdown && (
                    <div className="pl-4 space-y-1">
                      {serviceItems.map((service) => (
                        <Link
                          key={service.path}
                          to={service.path}
                          className="block py-1 text-xs text-gray-300 hover:text-[#5ba4c8]"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {service.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
